import {
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { Box, Button, LinearProgress } from '@mapped/rivet/dist/mui/material'
import { UserContext } from '../contexts/user'
import { useOrgsQuery } from '../components/user/switchOrgModal'
import { isOrgScopedPath, parseOrgPath, withOrgPath } from '../url/orgPath'
import { getActiveOrg } from '../url/orgNavigation'
import { decodeToken, getSessionAuthToken } from './auth0'
import { decidePathOrg, type MembershipState } from './pathOrg'
import { switchToOrg } from './switchOrg'

/**
 * The org this browser tab is reloading into after a successful switch.
 * Session storage, so it is per tab; written just before the reload and
 * trusted only for a short while after, because all it has to do is survive
 * that reload: if the tab comes back still outside the org, the switch did not
 * stick, and trying again by itself would reload forever. Anything older is a
 * leftover (a closed tab's sibling, a re-login) and must not deny a fresh
 * link without even trying.
 */
const ATTEMPT_KEY = 'mapped:url_org_attempt'
const ATTEMPT_TTL_MS = 30_000

function readAttempt(): string | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(ATTEMPT_KEY)
    if (!raw) return null
    const { orgId, at } = JSON.parse(raw) as { orgId?: unknown; at?: unknown }
    const fresh = typeof at === 'number' && Date.now() - at < ATTEMPT_TTL_MS
    return fresh && typeof orgId === 'string' ? orgId : null
  } catch {
    return null
  }
}

function writeAttempt(orgId: string | null) {
  try {
    if (orgId) {
      globalThis.sessionStorage?.setItem(
        ATTEMPT_KEY,
        JSON.stringify({ orgId, at: Date.now() })
      )
    } else {
      globalThis.sessionStorage?.removeItem(ATTEMPT_KEY)
    }
  } catch {
    // Without storage the loop guard is gone; the exchange still is not.
  }
}

/**
 * How long the exchange may take before the switch counts as failed. The
 * picker waits as long as it takes, but here the whole page is held behind a
 * progress bar, so a hung call would leave the reader with nothing to do.
 */
const EXCHANGE_TIMEOUT_MS = 20_000

/**
 * How long after asking for the reload the page may still be alive before we
 * say so. A `beforeunload` prompt the reader cancels (a running export sets
 * one) leaves the tokens switched but the page in the old org.
 */
const RELOAD_GRACE_MS = 4_000

/** Reload without `#fromLogin`, or `_app` would count a second login. */
function reloadClean() {
  if (window.location.hash === '#fromLogin') {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search
    )
  }
  window.location.reload()
}

/**
 * Back/Forward can restore a page from the browser's back-forward cache as it
 * was — rendered for the org it was in — after this tab has switched to
 * another (a switch writes this tab's tokens). Nothing re-renders, so the page
 * would show one org while its next token refresh queries another. Reload it
 * instead: the guard then sees the URL's org against the session's again.
 */
function reloadIfRestoredIntoAnotherOrg(event: PageTransitionEvent) {
  if (!event.persisted) return
  const sessionOrg = (decodeToken(getSessionAuthToken() ?? undefined) as { orgId?: string })
    .orgId
  if ((sessionOrg ?? null) !== getActiveOrg()) window.location.reload()
}

/**
 * Loaded on demand, and not only because it is rare: it renders the page
 * `Layout`, whose header reaches `pages/users` (through the profile modal),
 * which imports `withAuth` — which imports this. A static import would make
 * that a cycle that leaves `withAuth` uninitialised when `pages/users` runs.
 */
const loadAccessDenied = () =>
  import('./OrgAccessDenied').then((module) => module.OrgAccessDenied)

const OrgAccessDenied = dynamic(loadAccessDenied, {
  ssr: false,
  // Never a blank page: the progress bar until it arrives, and a way out if
  // it cannot (offline is exactly when a switch fails).
  loading: ({ error }) => (error ? <ReloadNotice /> : <OrgProgress />),
})

/**
 * Pages that take no part. The 404 page is shown for any unknown path, and
 * switching org (or rewriting the URL) to show "not found" helps no one. The
 * index page only forwards to `/explore`, a navigation that gains the prefix
 * on its own — canonicalising `/` too would race it (and `/orgs/<id>` is a
 * server redirect, so it would cost a full reload).
 */
const EXEMPT_PAGES: ReadonlySet<string> = new Set(['/', '/404', '/_error'])

/**
 * The org in the URL's path, on every signed-in page (it wraps `withAuth`'s
 * page). The rules, and why each exists, are in `decidePathOrg`; in short:
 *
 * - no prefix (every URL from before it existed): open in the current org, as
 *   always, and add the prefix to the address bar;
 * - the current org: nothing happens;
 * - an org the reader belongs to: switch through `switchToOrg` — the org
 *   picker's own exchange — and reload into it;
 * - anything else: an access page instead of the page, with the URL left as
 *   it is, so the reader can see which org it asked for.
 *
 * The page is held behind a progress bar while this is undecided, so the
 * reader never sees the wrong org's data flash up before the switch — and so
 * no page effect writes the URL while the prefix is being added.
 */
export const OrgPathGuard: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const router = useRouter()
  const { organization, isOBO, authToken } = useContext(UserContext)
  const [phase, setPhase] = useState<'idle' | 'switching' | 'reload-pending'>('idle')
  // A failed exchange, for this page's lifetime — it did not reload, so it
  // needs no storage (and must not outlive the page to deny a later link).
  const [failedOrg, setFailedOrg] = useState<string | null>(null)
  const handled = useRef<string | null>(null)

  const exempt = EXEMPT_PAGES.has(router.pathname)
  const pathOrg = exempt ? null : parseOrgPath(router.asPath).orgId
  const currentOrgId = organization?.id ?? undefined
  const foreign = !!pathOrg && pathOrg !== currentOrgId
  const needsMembership = foreign && !!currentOrgId && !isOBO
  const { orgs, isLoading, failed } = useOrgsQuery({ enabled: needsMembership })

  const members: MembershipState = isLoading
    ? { status: 'loading' }
    : failed
      ? { status: 'error' }
      : {
          status: 'ok',
          ids: orgs.map((org) => org.id).filter((id): id is string => !!id),
        }

  const decided = exempt
    ? ({ kind: 'none' } as const)
    : decidePathOrg({
        pathOrg,
        currentOrgId,
        isOBO,
        members,
        attempted: failedOrg === pathOrg ? failedOrg : readAttempt(),
      })
  // A signed-in page on a route that is never prefixed has nothing to add.
  const decision =
    decided.kind === 'canonicalize' && !isOrgScopedPath(router.asPath)
      ? ({ kind: 'none' } as const)
      : decided

  useEffect(() => {
    window.addEventListener('pageshow', reloadIfRestoredIntoAnotherOrg)
    return () => window.removeEventListener('pageshow', reloadIfRestoredIntoAnotherOrg)
  }, [])

  useEffect(() => {
    // Fetch the access page while the network is known to work, so a denial
    // never waits on it (or fails on it).
    if (foreign) void loadAccessDenied().catch(() => undefined)
  }, [foreign])

  useEffect(() => {
    // Arrived: the switch this tab made has landed, so the guard is spent.
    if (pathOrg && pathOrg === currentOrgId && readAttempt() === pathOrg) {
      writeAttempt(null)
    }
  }, [pathOrg, currentOrgId])

  // A fresh object every render; the effect keys on what it says instead.
  const decisionKey = JSON.stringify(decision)

  useEffect(() => {
    const key = `${decisionKey}@${router.asPath}`
    if (handled.current === key) return

    if (decision.kind === 'canonicalize') {
      const { pathname, search, hash } = window.location
      const current = pathname + search + hash
      const canonical = withOrgPath(current, decision.orgId)
      // Replace, not push: Back must not land on the unprefixed entry again.
      if (canonical !== current) {
        handled.current = key
        void router.replace(canonical, undefined, { shallow: true, scroll: false })
      }
      return
    }

    if (decision.kind !== 'switch' || !authToken) return
    handled.current = key
    const orgId = decision.orgId
    setPhase('switching')
    const timeout = new Promise<{ ok: false }>((resolve) =>
      setTimeout(() => resolve({ ok: false }), EXCHANGE_TIMEOUT_MS)
    )
    void Promise.race([switchToOrg(authToken, orgId), timeout]).then((result) => {
      if (result.ok) {
        writeAttempt(orgId)
        reloadClean()
        setTimeout(() => setPhase('reload-pending'), RELOAD_GRACE_MS)
        return
      }
      setFailedOrg(orgId)
      setPhase('idle')
    })
  }, [decisionKey, router, authToken])

  if (phase === 'reload-pending') return <ReloadNotice />

  if (decision.kind === 'deny' && phase === 'idle') {
    return (
      <OrgAccessDenied
        reason={decision.reason}
        requestedOrgId={pathOrg!}
        currentOrgName={organization?.name ?? undefined}
        onRetry={() => {
          writeAttempt(null)
          handled.current = null
          setFailedOrg(null)
        }}
      />
    )
  }

  if (decision.kind !== 'none' || phase === 'switching') return <OrgProgress />

  return <>{children}</>
}

const FULL_SCREEN = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'center',
  background: 'white',
  zIndex: 10000,
} as const

const OrgProgress: FunctionComponent = () => (
  <Box role="progressbar" aria-label="Opening the organization in this link" sx={FULL_SCREEN}>
    <LinearProgress variant="indeterminate" sx={{ width: 200 }} />
  </Box>
)

/** The switch landed but the page did not reload (or could not load). */
const ReloadNotice: FunctionComponent = () => (
  <Box role="alert" sx={FULL_SCREEN}>
    <p>Reload the page to finish opening this organization.</p>
    <Button variant="contained" onClick={reloadClean}>
      Reload
    </Button>
  </Box>
)
